import type {
  ProjectGraphEdge,
  ProjectGraphEdgeKind,
  ProjectGraphNode,
  ProjectGraphNodeKind,
  ProjectGraphResponse,
} from '@avisoma/shared';
import { kindRank } from './concentricLayout';
import { indexOf, pairKey } from './graphIndex';

export { nodeLabel } from './nodeLabel';

/** A bucket of children with at least this many members draws as one group node. */
export const GROUP_MIN = 6;

/**
 * An expanded group shows this many of its best-connected members per page asked for (one to start, one more
 * per "Show 30 more"), plus the ones the user revealed.
 */
export const GROUP_PAGE = 30;

/** Children are grouped by kind; references by AD/SB/task, documents by category. */
export type GroupKey =
  'engine' | 'aircraft' | 'part' | 'event' | 'ipc' | 'ref:ad' | 'ref:sb' | 'ref:task' | `document:${string}`;

export type GroupViewNode = {
  /** `group:<parentId>|<groupKey>`. */
  id: string;
  kind: 'group';
  parentId: string;
  groupKey: GroupKey;
  label: string;
  /** Every candidate child of the parent in this group, including members shown under a nearer node. */
  total: number;
  /** Members shown under this group: 0 while it is collapsed. */
  shown: number;
  /** Members still not in the view that another page would show: 0 while the group is collapsed. */
  remaining: number;
  /** Every member, best-connected first. */
  memberIds: string[];
};

/** The synthetic root of the overview (no focus): every node of the graph is its candidate child. */
export type ProjectViewNode = { id: `project:${string}`; kind: 'project'; label: string };

export type ViewNode = ProjectGraphNode | GroupViewNode | ProjectViewNode;

type ProjectGraphEdgeRole = NonNullable<ProjectGraphEdge['role']>;

/** Real edges keep their id, kind and role; synthetic edges run parent → group, group → member, and project → child. */
export type ViewEdge = {
  id: string;
  from: string;
  to: string;
  kind?: ProjectGraphEdgeKind;
  role?: ProjectGraphEdgeRole;
  synthetic: boolean;
};

export type ViewState = {
  /** The focus; `null` (or an id not in the graph) roots the view at the project. */
  rootId: string | null;
  /** Real and group node ids whose children show. The root is always expanded. */
  expandedIds: ReadonlySet<string>;
  /** Group members the user picked from a group's list; they show beside the group's top pages. */
  revealedIds: ReadonlySet<string>;
  /** The pages of GROUP_PAGE members each expanded group shows, by group id; 1 when absent. */
  pageByGroup: ReadonlyMap<string, number>;
  hiddenKinds: ReadonlySet<ProjectGraphNodeKind>;
  hiddenCategories: ReadonlySet<string>;
};

export type VisibleGraph = {
  nodes: ViewNode[];
  /** Every visible edge: the tree edges plus the other real edges between visible nodes, sorted by id. */
  edges: ViewEdge[];
  /** One edge into each visible node but the root, from the node it is placed under. The layout uses these. */
  treeEdges: ViewEdge[];
  /** Visible nodes with at least one child placed under them: the parent end of a tree edge. */
  parentIds: ReadonlySet<string>;
  /** Visible nodes that would show children: a real node with a candidate child, a group with members left to show. */
  expandableIds: ReadonlySet<string>;
  /** The node each visible node but the root is placed under: the parent end of its tree edge. */
  parentOf: ReadonlyMap<string, string>;
};

/** The node type filters, in the order the filter panel lists them, each with its checkbox label. */
export const KIND_FILTERS: { kind: ProjectGraphNodeKind; label: string }[] = [
  { kind: 'engine', label: 'Engines' },
  { kind: 'aircraft', label: 'Aircraft' },
  { kind: 'part', label: 'Parts' },
  { kind: 'reference', label: 'References' },
  { kind: 'document', label: 'Documents' },
  { kind: 'event', label: 'Events' },
  { kind: 'ipc', label: 'IPC positions' },
];

/** `?node=project` roots the view at the project. */
export const PROJECT_ROOT_PARAM = 'project';

const UNCATEGORISED = 'Uncategorised';
const DOCUMENT_PREFIX = 'document:';

const GROUP_LABELS: Record<string, string> = {
  engine: 'Engines',
  aircraft: 'Aircraft',
  part: 'Parts',
  event: 'Events',
  ipc: 'IPC positions',
  'ref:ad': 'AD references',
  'ref:sb': 'SB references',
  'ref:task': 'Tasks',
};

/** "Engines", "Parts", "AD references", …; a document group is named after its category. */
export function groupLabel(key: GroupKey): string {
  if (key.startsWith(DOCUMENT_PREFIX)) return key.slice(DOCUMENT_PREFIX.length);
  return GROUP_LABELS[key] ?? key;
}

/** The group a node falls in under its parent. Kinds added after this release are never grouped. */
function groupKeyOf(node: ProjectGraphNode): GroupKey | null {
  switch (node.kind) {
    case 'engine':
    case 'aircraft':
    case 'part':
    case 'event':
    case 'ipc':
      return node.kind;
    case 'reference':
      return `ref:${node.refKind}`;
    case 'document':
      return `${DOCUMENT_PREFIX}${node.category ?? UNCATEGORISED}`;
    default:
      return null;
  }
}

export const projectNodeId = (projectId: string): `project:${string}` => `project:${projectId}`;

/** The node the view is rooted at: the focus when it names a node, else the synthetic project node. */
export function resolveRootId(graph: ProjectGraphResponse, rootId: string | null): string {
  return rootId !== null && indexOf(graph).nodesById.has(rootId) ? rootId : projectNodeId(graph.projectId);
}

/**
 * The node the rings are laid out around: the selection when `centreOnSelect` is on and a node is selected, else
 * the root. The root stays the origin of the exploration (the breadcrumb, the tree); only the rings move.
 */
export const layoutCentre = (selectedId: string | null, rootId: string, centreOnSelect: boolean): string =>
  centreOnSelect && selectedId !== null ? selectedId : rootId;

/**
 * The view move that goes with a click on a node: show it with its ring 1 when the rings re-centre on it, else
 * keep it where it is on screen while its children appear.
 */
export const activationRequest = (nodeId: string, centreOnSelect: boolean) =>
  centreOnSelect ? { kind: 'centre' as const, nodeId } : { kind: 'anchor' as const, nodeIds: [nodeId] };

/**
 * The view move that goes with clearing the selection: show the root with its ring 1 when the rings return to it;
 * none when they never left it.
 */
export const deselectionRequest = (rootId: string, centreOnSelect: boolean) =>
  centreOnSelect ? { kind: 'centre' as const, nodeId: rootId } : null;

/** The focus named by `?node=`: `project` is the overview, a node id is that node, anything else the entry node. */
export function rootFromParam(graph: ProjectGraphResponse, param: string | null): string | null {
  if (param === PROJECT_ROOT_PARAM) return null;
  const { nodesById } = indexOf(graph);
  const inGraph = (id: string | null): id is string => id !== null && nodesById.has(id);
  if (inGraph(param)) return param;
  return inGraph(graph.entryNodeId) ? graph.entryNodeId : null;
}

export const groupNodeId = (parentId: string, key: GroupKey) => `group:${parentId}|${key}`;

const byId = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

type Bucket = { key: GroupKey | null; kind: string; memberIds: string[] };

/** Buckets the children by group key; a kind without a key gets one bucket per node. Sorted by kind, then key. */
function bucketChildren(childIds: string[], nodesById: ReadonlyMap<string, ProjectGraphNode>): Bucket[] {
  const keyed = new Map<GroupKey, Bucket>();
  const loose: Bucket[] = [];
  for (const id of childIds) {
    const node = nodesById.get(id);
    if (!node) continue;
    const key = groupKeyOf(node);
    if (key === null) {
      loose.push({ key, kind: node.kind, memberIds: [id] });
      continue;
    }
    const bucket = keyed.get(key) ?? { key, kind: node.kind, memberIds: [] };
    bucket.memberIds.push(id);
    keyed.set(key, bucket);
  }
  const sortKey = (b: Bucket) => b.key ?? b.memberIds[0];
  return [...keyed.values(), ...loose].sort(
    (a, b) => kindRank(a.kind) - kindRank(b.kind) || byId(sortKey(a), sortKey(b)),
  );
}

const realEdge = (e: ProjectGraphEdge): ViewEdge => ({
  id: e.id,
  from: e.from,
  to: e.to,
  kind: e.kind,
  ...(e.role ? { role: e.role } : {}),
  synthetic: false,
});
const syntheticEdge = (from: string, to: string): ViewEdge => ({
  id: `synthetic:${from}->${to}`,
  from,
  to,
  synthetic: true,
});
const byLabelThenId = (a: GroupViewNode, b: GroupViewNode) =>
  byId(a.label, b.label) || byId(a.groupKey, b.groupKey) || byId(a.id, b.id);

/** The nodes of one BFS depth: real nodes run first, in kind-then-id order, then groups, in label-then-key order. */
type Level = { real: string[]; groups: GroupViewNode[] };

/**
 * The drill-down view: a breadth-first walk from the root over the visible nodes only.
 * - An expanded real node (the root always) takes as candidates its neighbours that are neither hidden nor
 *   already visible. A bucket of GROUP_MIN candidates or more of one group key draws as a group node; a smaller
 *   bucket shows its members right away, joined by their real edge.
 * - An expanded group shows its best-connected members that are not yet visible, GROUP_PAGE per page asked for
 *   (see `pageByGroup`), plus the revealed ones, joined by synthetic edges. A collapsed group places nothing, so its members stay free for a node
 *   further out; `total` still counts them.
 * - The project root (no focus) takes every node of the graph as a candidate, joined by synthetic edges.
 * - A group left with nothing to show (every member placed under another node) is dropped with its edge.
 * Every real edge between two visible real nodes shows, except a group's parent → member edge, which the
 * group stands for.
 */
export function selectVisible(graph: ProjectGraphResponse, state: ViewState): VisibleGraph {
  const { nodesById, adjacency, degree, edgeOfPair, outgoing } = indexOf(graph);
  const shownKind = (node: ProjectGraphNode) =>
    !state.hiddenKinds.has(node.kind) &&
    !(node.kind === 'document' && node.category !== null && state.hiddenCategories.has(node.category));
  const rootId = resolveRootId(graph, state.rootId);
  const rootNode = nodesById.get(rootId);
  const root: ViewNode = rootNode ?? {
    id: projectNodeId(graph.projectId),
    kind: 'project',
    label: `Project ${graph.projectId.slice(0, 8)}`,
  };
  const rank = (a: string, b: string) => (degree.get(b) ?? 0) - (degree.get(a) ?? 0) || byId(a, b);
  const byKindThenId = (a: string, b: string) =>
    kindRank(nodesById.get(a)?.kind ?? '') - kindRank(nodesById.get(b)?.kind ?? '') || byId(a, b);

  const visible = new Set([rootId]);
  const nodes: ViewNode[] = [root];
  const treeEdges: ViewEdge[] = [];
  const candidatesByParent = new Map<string, string[]>();
  // The node each visible node is placed under. A real tree edge keeps its data direction (a document → the
  // engine it names, say), so the edge alone does not tell the parent.
  const parentOf = new Map<string, string>();
  const standIns = new Set<string>();
  const place = (id: string, parentId: string, edge: ViewEdge, next: Level) => {
    const node = nodesById.get(id);
    if (!node) return;
    visible.add(id);
    parentOf.set(id, parentId);
    nodes.push(node);
    treeEdges.push(edge);
    next.real.push(id);
  };
  const candidatesOf = (parentId: string) =>
    (parentId === rootId && !rootNode ? graph.nodes.map((n) => n.id) : [...(adjacency.get(parentId) ?? [])]).filter(
      (id) => {
        const node = nodesById.get(id);
        return node !== undefined && !visible.has(id) && shownKind(node);
      },
    );

  const expandReal = (parentId: string, next: Level) => {
    const candidates = candidatesOf(parentId);
    candidatesByParent.set(parentId, candidates);
    if (parentId !== rootId && !state.expandedIds.has(parentId)) return;
    for (const bucket of bucketChildren(candidates, nodesById)) {
      if (bucket.key === null || bucket.memberIds.length < GROUP_MIN) {
        [...bucket.memberIds].sort(byKindThenId).forEach((id) => {
          const edge = edgeOfPair.get(pairKey(parentId, id));
          place(id, parentId, edge ? realEdge(edge) : syntheticEdge(parentId, id), next);
        });
        continue;
      }
      const group: GroupViewNode = {
        id: groupNodeId(parentId, bucket.key),
        kind: 'group',
        parentId,
        groupKey: bucket.key,
        label: groupLabel(bucket.key),
        total: bucket.memberIds.length,
        shown: 0,
        remaining: 0,
        memberIds: [...bucket.memberIds].sort(rank),
      };
      nodes.push(group);
      parentOf.set(group.id, parentId);
      treeEdges.push(syntheticEdge(parentId, group.id));
      next.groups.push(group);
    }
  };

  const expandGroup = (group: GroupViewNode, next: Level) => {
    const free = group.memberIds.filter((id) => !visible.has(id));
    const limit = GROUP_PAGE * (state.pageByGroup.get(group.id) ?? 1);
    const shown = state.expandedIds.has(group.id) ? free.filter((id, i) => i < limit || state.revealedIds.has(id)) : [];
    shown.forEach((id) => {
      place(id, group.id, syntheticEdge(group.id, id), next);
      standIns.add(pairKey(group.parentId, id));
    });
    group.shown = shown.length;
  };

  const levels: Level[] = [{ real: [rootId], groups: [] }];
  // The array iterator reads the length on every step, so it also visits the levels pushed below.
  for (const level of levels) {
    const next: Level = { real: [], groups: [] };
    [...level.real].sort(byKindThenId).forEach((id) => expandReal(id, next));
    [...level.groups].sort(byLabelThenId).forEach((group) => expandGroup(group, next));
    if (next.real.length > 0 || next.groups.length > 0) levels.push(next);
  }

  // A group whose members all sit under other nodes has nothing to show: drop it and its edge. A member
  // placed under the group makes `shown` positive, so the test needs only the visible set.
  const empty = new Set(
    nodes.flatMap((n) =>
      n.kind === 'group' && n.shown === 0 && n.memberIds.every((id) => visible.has(id)) ? [n.id] : [],
    ),
  );
  const keptNodes = nodes.filter((n) => !empty.has(n.id));
  // Counted once the walk is done: a member a later node placed is in the view, so no page would show it.
  for (const n of keptNodes)
    if (n.kind === 'group' && state.expandedIds.has(n.id))
      n.remaining = n.memberIds.reduce((count, id) => (visible.has(id) ? count : count + 1), 0);
  const keptTreeEdges = treeEdges.filter((e) => !empty.has(e.to));
  // Expandable once the walk is done: a node with children placed under it, or with a candidate (a member,
  // for a group) that is still not visible. A candidate that a later node placed does not count.
  const keptParentOf = new Map([...parentOf].filter(([child]) => !empty.has(child)));
  const parentIds = new Set(keptParentOf.values());
  const expandable = new Set(
    keptNodes.flatMap((n) => {
      const pending = n.kind === 'group' ? n.memberIds : (candidatesByParent.get(n.id) ?? []);
      return parentIds.has(n.id) || pending.some((id) => !visible.has(id)) ? [n.id] : [];
    }),
  );

  // Each real edge between visible nodes, read from its `from` end so it counts once.
  const others = [...visible].flatMap((id) =>
    (outgoing.get(id) ?? []).flatMap((e) =>
      visible.has(e.to) && nodesById.has(e.to) && !standIns.has(pairKey(e.from, e.to)) ? [realEdge(e)] : [],
    ),
  );
  const edges = [...new Map([...keptTreeEdges, ...others].map((e) => [e.id, e])).values()].sort((a, b) =>
    byId(a.id, b.id),
  );
  return {
    nodes: keptNodes,
    edges,
    treeEdges: keptTreeEdges,
    parentIds,
    expandableIds: expandable,
    parentOf: keptParentOf,
  };
}

/** A node and every visible node whose chain of placement parents reaches it. */
export function branchIds(parentOf: ReadonlyMap<string, string>, nodeId: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const [child, parent] of parentOf) {
    const siblings = children.get(parent);
    if (siblings) siblings.push(child);
    else children.set(parent, [child]);
  }
  const branch = new Set([nodeId]);
  // A set iterator also visits the ids added during the walk.
  for (const id of branch) for (const child of children.get(id) ?? []) branch.add(child);
  return branch;
}

/**
 * The node can collapse: it is expanded, not the root, and shows children (a real node with a child placed
 * under it, a group with members shown). The canvas chevron and the sidebar Collapse action both use this.
 */
export function canCollapse(
  node: ViewNode,
  rootId: string,
  expandedIds: ReadonlySet<string>,
  parentIds: ReadonlySet<string>,
): boolean {
  if (node.id === rootId || !expandedIds.has(node.id)) return false;
  return node.kind === 'group' ? node.shown > 0 : parentIds.has(node.id);
}

const visibleIdsOf = (view: VisibleGraph) => new Set(view.nodes.map((n) => n.id));

/**
 * Collapses a node: removes it from `expandedIds`, with every expansion that the collapse itself hides.
 * Expansions already hidden (by a filter, say) are kept, and so are the revealed members.
 */
export function collapseNode(graph: ProjectGraphResponse, state: ViewState, id: string): ReadonlySet<string> {
  const before = visibleIdsOf(selectVisible(graph, state));
  const collapsed = new Set([...state.expandedIds].filter((e) => e !== id));
  const after = visibleIdsOf(selectVisible(graph, { ...state, expandedIds: collapsed }));
  return new Set([...collapsed].filter((e) => !before.has(e) || after.has(e)));
}

/**
 * Collapses a node as `collapseNode` does, and forgets the pages asked for of every group the collapse closed,
 * so a group opened again starts at one page.
 */
export function collapseState(graph: ProjectGraphResponse, state: ViewState, id: string): ViewState {
  const expandedIds = collapseNode(graph, state, id);
  return { ...state, expandedIds, pageByGroup: new Map([...state.pageByGroup].filter(([g]) => expandedIds.has(g))) };
}

/** One more page of GROUP_PAGE members for a group. */
export function withNextPage(pageByGroup: ReadonlyMap<string, number>, groupId: string): ReadonlyMap<string, number> {
  return new Map(pageByGroup).set(groupId, (pageByGroup.get(groupId) ?? 1) + 1);
}

/**
 * The state that shows `targetId`, a neighbour of the visible node `fromId`: `fromId` expanded, and when the
 * target is still behind a group (under `fromId` first, else anywhere), that group expanded with the target
 * revealed. Unchanged when the target is already visible, or when no state can show it (a filter hides it).
 */
export function revealNeighbour(
  graph: ProjectGraphResponse,
  state: ViewState,
  fromId: string,
  targetId: string,
): ViewState {
  const shows = (s: ViewState) => visibleIdsOf(selectVisible(graph, s)).has(targetId);
  if (shows(state)) return state;
  const expanded = { ...state, expandedIds: new Set([...state.expandedIds, fromId]) };
  const view = selectVisible(graph, expanded);
  if (visibleIdsOf(view).has(targetId)) return expanded;
  const groups = view.nodes.flatMap((n) => (n.kind === 'group' && n.memberIds.includes(targetId) ? [n] : []));
  const group = groups.find((g) => g.parentId === fromId) ?? groups[0];
  if (!group) return state;
  const revealed = {
    ...expanded,
    expandedIds: new Set([...expanded.expandedIds, group.id]),
    revealedIds: new Set([...state.revealedIds, targetId]),
  };
  return shows(revealed) ? revealed : state;
}

/**
 * The state that shows `targetId` wherever it sits: unchanged when it already shows, else what `revealNeighbour`
 * gives from the root, then from each visible neighbour of the target in id order. `null` when none of them can
 * show it (a filter hides it, or no visible node leads to it under the current focus).
 */
export function revealAnywhere(graph: ProjectGraphResponse, state: ViewState, targetId: string): ViewState | null {
  const shown = visibleIdsOf(selectVisible(graph, state));
  if (shown.has(targetId)) return state;
  const neighbours = [...(indexOf(graph).adjacency.get(targetId) ?? [])].filter((id) => shown.has(id)).sort(byId);
  for (const fromId of [resolveRootId(graph, state.rootId), ...neighbours]) {
    const next = revealNeighbour(graph, state, fromId, targetId);
    if (next !== state) return next;
  }
  return null;
}

/**
 * The filter that keeps `node` out of the view: `kind` when its node type is hidden, else `category` when it
 * is a document of a hidden category. `null` when no filter hides it, and for the view's root (`rootId`, see
 * `resolveRootId`), which always shows. Only the node itself counts: a neighbour of a visible node needs no
 * other node to show it.
 */
export function hiddenBy(node: ProjectGraphNode, state: ViewState, rootId: string): 'kind' | 'category' | null {
  if (node.id === rootId) return null;
  if (state.hiddenKinds.has(node.kind)) return 'kind';
  return node.kind === 'document' && node.category !== null && state.hiddenCategories.has(node.category)
    ? 'category'
    : null;
}

/** The placement chain of a visible node: the root first, the node last. Just the node for the root. */
export function pathToRoot(parentOf: ReadonlyMap<string, string>, nodeId: string): string[] {
  const path = [nodeId];
  // One step per entry at most: the map is a tree, so the walk ends at the root well before that.
  for (const _entry of parentOf) {
    const parent = parentOf.get(path[0]);
    if (parent === undefined) break;
    path.unshift(parent);
  }
  return path;
}

/** The tree edge into each node of `path` but the first: the edges that join the path on the canvas. */
export function pathEdgeIds(treeEdges: readonly ViewEdge[], path: readonly string[]): Set<string> {
  const ids = new Set<string>();
  path.slice(1).forEach((child, i) => {
    const parent = path[i];
    // A real tree edge keeps its data direction, so either end can be the parent.
    const edge = treeEdges.find((e) => (e.from === parent && e.to === child) || (e.from === child && e.to === parent));
    if (edge) ids.add(edge.id);
  });
  return ids;
}

/** The groups a node shows once expanded; none when the node is not in the view. */
export function childGroups(graph: ProjectGraphResponse, state: ViewState, nodeId: string): GroupViewNode[] {
  const expanded = { ...state, expandedIds: new Set([...state.expandedIds, nodeId]) };
  return selectVisible(graph, expanded).nodes.flatMap((n) => (n.kind === 'group' && n.parentId === nodeId ? [n] : []));
}
